import type { Principal, Role } from '../../controlled/commonTypes.tsx';
import type { PrincipalsMap } from '../principalsMap.ts';

import type { useUpdateContentDirectRestrictionsMutation$variables as MutationVariables } from './__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';

export type ConfluenceDirectRestrictionPrincipalType = 'USER' | 'GROUP';
export type ConfluenceDirectRestrictionPermission = 'view' | 'edit';

type PermissionOrNone = ConfluenceDirectRestrictionPermission | null;

function mapPrincipalTypeToDirectRestrictionPrincipalType(
	principalType: Principal['type'],
): ConfluenceDirectRestrictionPrincipalType {
	switch (principalType) {
		case 'group':
		case 'team':
			return 'GROUP';
		case 'user':
		case 'external_user':
		default:
			return 'USER';
	}
}

function roleToPermission(role: Role): PermissionOrNone {
	switch (role) {
		case 'can-view':
			return 'view';
		case 'can-edit':
			return 'edit';
		case 'no-access':
			return null;
		default:
			return null;
	}
}

export function buildUpdateContentDirectRestrictionsBulkRemoveVariables({
	cloudId,
	contentId,
	includeInvites,
	principals,
	principalRoles,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principals: Principal[];
	principalRoles: Map<string, Role>;
}): MutationVariables {
	const viewRestrictions: Array<{
		principalId: string;
		principalType: ConfluenceDirectRestrictionPrincipalType;
	}> = [];
	const editRestrictions: Array<{
		principalId: string;
		principalType: ConfluenceDirectRestrictionPrincipalType;
	}> = [];

	for (const principal of principals) {
		const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
		const role = principalRoles.get(principal.id);
		const permission = role ? roleToPermission(role) : null;

		if (permission === 'view') {
			viewRestrictions.push({ principalId: principal.id, principalType });
		} else if (permission === 'edit') {
			editRestrictions.push({ principalId: principal.id, principalType });
		}
	}

	const remove: NonNullable<MutationVariables['input']['remove']> = {};
	if (viewRestrictions.length > 0) {
		remove.view = { restrictions: viewRestrictions };
	}
	if (editRestrictions.length > 0) {
		remove.edit = { restrictions: editRestrictions };
	}

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			...(Object.keys(remove).length ? { remove } : {}),
		},
	};
}

export function buildUpdateContentDirectRestrictionsRemoveVariables({
	cloudId,
	contentId,
	includeInvites,
	principal,
	role,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principal: Principal;
	role: Role;
}): MutationVariables {
	const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
	const permission = roleToPermission(role);
	const restriction = {
		principalId: principal.id,
		principalType,
	} as const;

	// Only remove from the role the principal actually holds (edit or view, not both)
	const remove: NonNullable<MutationVariables['input']['remove']> = {};
	if (permission) {
		remove[permission] = { restrictions: [restriction] };
	}

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			...(Object.keys(remove).length ? { remove } : {}),
		},
	};
}

// Transform a single principal's role change into add/remove buckets for the direct restrictions mutation
export function buildUpdateContentDirectRestrictionsVariables({
	cloudId,
	contentId,
	includeInvites,
	principal,
	previousRole,
	nextRole,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principal: Principal;
	previousRole: Role;
	nextRole: Role;
}): MutationVariables {
	const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
	const previousPermission = roleToPermission(previousRole);
	const nextPermission = roleToPermission(nextRole);

	const restriction = {
		principalId: principal.id,
		principalType,
	} as const;

	const add: NonNullable<MutationVariables['input']['add']> = {};
	const remove: NonNullable<MutationVariables['input']['remove']> = {};

	// When changing to no-access (removing the principal), we need to explicitly remove them.
	// When changing between access levels (view <-> edit), the backend handles the removal
	// automatically, so we only need to add to the new role.
	if (nextPermission) {
		add[nextPermission] = { restrictions: [restriction] };
	} else if (previousPermission) {
		// nextRole is 'no-access', so remove from the previous role
		remove[previousPermission] = { restrictions: [restriction] };
	}

	const input: MutationVariables['input'] = {
		contentId,
		includeInvites,
		...(Object.keys(add).length ? { add } : {}),
		...(Object.keys(remove).length ? { remove } : {}),
	};

	return {
		cloudId,
		input,
	};
}

// Transform group of restrictions into add/remove buckets, bucketing by role -> permission, then into 'add' or 'remove'
export function buildUpdateContentDirectRestrictionsFromStagedPrincipal({
	cloudId,
	contentId,
	includeInvites,
	principalsMap,
	action,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principalsMap: PrincipalsMap;
	action: 'add' | 'remove';
}): MutationVariables {
	const viewRestrictions: Array<{
		principalId: string;
		principalType: ConfluenceDirectRestrictionPrincipalType;
	}> = [];
	const editRestrictions: Array<{
		principalId: string;
		principalType: ConfluenceDirectRestrictionPrincipalType;
	}> = [];

	for (const [principalId, entry] of principalsMap.entries()) {
		const { principal, role } = entry;
		const permission = roleToPermission(role);
		if (!permission) continue;
		const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
		const restriction = { principalId, principalType } as const;
		if (permission === 'view') {
			viewRestrictions.push(restriction);
		} else if (permission === 'edit') {
			editRestrictions.push(restriction);
		}
	}

	const add: NonNullable<MutationVariables['input']['add']> | undefined =
		action === 'add'
			? {
					...(viewRestrictions.length ? { view: { restrictions: viewRestrictions } } : {}),
					...(editRestrictions.length ? { edit: { restrictions: editRestrictions } } : {}),
				}
			: undefined;

	const remove: NonNullable<MutationVariables['input']['remove']> | undefined =
		action === 'remove'
			? {
					...(viewRestrictions.length ? { view: { restrictions: viewRestrictions } } : {}),
					...(editRestrictions.length ? { edit: { restrictions: editRestrictions } } : {}),
				}
			: undefined;

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			...(add ? { add } : {}),
			...(remove ? { remove } : {}),
		},
	};
}
